import { RtFraction } from "@aprao/common/src/runtypes/common.js";
import type { ShapeAppraisalDocument } from "@aprao/model/src/shapes/ShapeAppraisal.js";
import type { ShapeSalesTaxConfiguration } from "@aprao/model/src/shapes/ShapeProject.js";
import { css } from "@emotion/react";
import { toJS } from "mobx";
import { mapOrForResult } from "option-t/plain_result";
import { useEffect, useMemo } from "react";
import { useErrorBoundary } from "react-error-boundary";

import { useI18n } from "../common/ApraoI18n.js";
import type { AnalysisOutputKind } from "../common/sensitivity/outputProducersRegistry.js";
import { SensitivityTable } from "../common/sensitivity/SensitivityTable.js";
import type { CloneableAnalysisParam } from "../common/sensitivity/sensitivityWorker.js";
import { useSensitivityComputation } from "../common/sensitivity/useSensitivityData.js";
import { useNumberUnits } from "../common/useNumberUnits.js";
import { Section } from "./Section.js";

export const SensitivitySection = (props: {
  appraisal: ShapeAppraisalDocument;
  salesTaxConfiguration: ShapeSalesTaxConfiguration | null;
  isOperatedAssetSensitivityAnalysesEnabled: boolean;
  onReady: () => void;
}) => {
  const {
    appraisal,
    salesTaxConfiguration,
    isOperatedAssetSensitivityAnalysesEnabled,
    onReady,
  } = props;

  const { showBoundary } = useErrorBoundary();

  const [{ value: sensitivityResults, error: sensitivityError }, compute] =
    useSensitivityComputation();

  // We can only show residual value sensitivity if there is one and only
  // on site
  const canAnalyseResidualValue =
    appraisal.stages.purchase.sitePurchases.length === 1;

  const analyses = useMemo(() => {
    const genericOutputKinds: Array<AnalysisOutputKind> =
      canAnalyseResidualValue ?
        ["roc", "profit", "residual"]
      : ["roc", "profit"];

    const genericAnalyses: Array<CloneableAnalysisParam<AnalysisOutputKind>> =
      // one analysis by output type
      genericOutputKinds.map((outputKind) => ({
        xAxis: {
          kind: "gdv",
          range: [RtFraction.parse("-20/100"), RtFraction.parse("20/100")],
        },
        yAxis: {
          kind: "buildCosts",
          range: [RtFraction.parse("20/100"), RtFraction.parse("-20/100")],
        },
        output: outputKind,
      }));

    return isOperatedAssetSensitivityAnalysesEnabled ?
        genericAnalyses.concat([
          {
            xAxis: {
              kind: "buildCosts",
              range: [RtFraction.parse("-20/100"), RtFraction.parse("20/100")],
            },
            yAxis: {
              kind: "rent",
              range: [RtFraction.parse("20/100"), RtFraction.parse("-20/100")],
            },
            output: "residual",
          },
          {
            xAxis: {
              kind: "yield",
              range: [RtFraction.parse("-1/1"), RtFraction.parse("1/1")],
            },
            yAxis: {
              kind: "rent",
              range: [RtFraction.parse("20/100"), RtFraction.parse("-20/100")],
            },
            output: "residual",
          },
          {
            xAxis: {
              kind: "buildCosts",
              range: [RtFraction.parse("-20/100"), RtFraction.parse("20/100")],
            },
            yAxis: {
              kind: "yield",
              range: [RtFraction.parse("1/1"), RtFraction.parse("-1/1")],
            },
            output: "residual",
          },
        ])
      : genericAnalyses;
  }, [canAnalyseResidualValue, isOperatedAssetSensitivityAnalysesEnabled]);

  useEffect(() => {
    compute({
      appraisalDocument: toJS(appraisal),
      salesTaxConfiguration: toJS(salesTaxConfiguration),
      analyses,
      steps: 9,
    });
  }, [compute, analyses, appraisal, salesTaxConfiguration]);

  useEffect(() => {
    if (sensitivityError != null) {
      showBoundary(sensitivityError);
    }
  }, [showBoundary, sensitivityError]);

  const { msg } = useI18n();
  const { currency } = useNumberUnits();

  if (sensitivityResults == null) {
    return null;
  }

  return (
    <Section
      title="Sensitivity Analysis"
      data-testid="ss__section"
      ref={(elem) => {
        if (elem != null) {
          onReady();
        }
      }}
    >
      {sensitivityResults.map((result, resultIndex) =>
        mapOrForResult(result, null, (sensitivity) => (
          <SensitivityTable
            css={css`
              margin-block-end: 24px;

              font-size: calc(0.9 * var(--report-root-font-size));
            `}
            key={resultIndex}
            caption={
              <caption
                css={css`
                  font-size: calc(1 * var(--report-root-font-size));
                  font-weight: bold;
                  text-align: start;
                `}
              >
                {msg(`sensitivity.${sensitivity.analysis.output}Caption`)},
                amounts in {currency.code}
              </caption>
            }
            sensitivity={sensitivity}
          />
        )),
      )}
    </Section>
  );
};
